import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useAuth } from "../../context/AuthContext";
import ProfileModal from './ProfileModal';

const BASE = "https://us-central1-inprojuy-f1173.cloudfunctions.net/app";

function AutoexcluidosGallery() {
  const [autoexcluidos, setAutoexcluidos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage] = useState(8);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState("");
  const [selectedUserData, setSelectedUserData] = useState(null);

  const [confirmando, setConfirmando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, message, detail? }

  const auth = useAuth();

  const isDesktopOrLaptop = useMediaQuery({ query: '(min-device-width: 1224px)' });
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${BASE}/autoexcluidos`);
        setAutoexcluidos(response.data);
      } catch (error) {
        console.error("Error fetching autoexcluidos data", error);
      }
    };
    fetchData();
  }, []);

  const indexOfLastPhoto = currentPage * photosPerPage;
  const indexOfFirstPhoto = indexOfLastPhoto - photosPerPage;
  const currentPhotos = autoexcluidos.slice(indexOfFirstPhoto, indexOfLastPhoto);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleShow = async (photoUrl, dni) => {
    setSelectedPhoto(photoUrl);
    setSelectedUserData(null);
    setFeedback(null);
    setConfirmando(false);
    setShowModal(true);
    try {
      const { data } = await axios.get(`${BASE}/autoexcluidos/${dni}`);
      setSelectedUserData(data);
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedUserData(null);
    setFeedback(null);
    setConfirmando(false);
  };

  const registrarIntento = async (dryRun = false) => {
    if (!selectedUserData?.id) return;

    const setSetter = dryRun ? setDryRunLoading : setLoading;
    setSetter(true);
    setFeedback(null);

    try {
      const { data } = await axios.post(`${BASE}/registrar-intento-ingreso`, {
        autoexcluidoId: selectedUserData.id,
        dryRun,
      });

      if (dryRun) {
        setFeedback({
          type: 'info',
          message: `[Prueba] Se enviaría a ${data.destinatarios.length} destinatario(s):`,
          detail: data.destinatarios.join(', '),
        });
      } else {
        setFeedback({
          type: 'success',
          message: `Intento registrado. Notificaciones enviadas a ${data.correosEnviados.length} destinatario(s).`,
        });
      }
      setConfirmando(false);
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Error desconocido";
      setFeedback({ type: 'danger', message: `Error: ${msg}` });
      setConfirmando(false);
    } finally {
      setSetter(false);
    }
  };

  const getColSize = () => {
    if (isDesktopOrLaptop) return 3;
    if (isTabletOrMobile) return 4;
    return 4;
  };

  return (
    <div>
      <Container className="mt-4">
        <Row>
          {currentPhotos.map((autoexcluido) => (
            <Col key={autoexcluido.dni} xs={12} md={getColSize()} className="mb-4">
              <div className="photo-container">
                <div className="caption">
                  <p className="caption-text">{autoexcluido.nombre}</p>
                </div>
                <img
                  src={autoexcluido.fotos.filter((foto) => foto !== null).pop() || ""}
                  alt={`Autoexcluido ${autoexcluido.dni}`}
                  className="img-fluid"
                  style={{ objectFit: "cover", height: "100%", width: "100%" }}
                  onClick={() => handleShow(
                    autoexcluido.fotos.filter((foto) => foto !== null).pop() || "",
                    autoexcluido.dni
                  )}
                />
              </div>
            </Col>
          ))}
        </Row>
        <Pagination
          photosPerPage={photosPerPage}
          totalPhotos={autoexcluidos.length}
          paginate={paginate}
          currentPage={currentPage}
        />
      </Container>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Foto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={selectedPhoto}
            alt="Foto"
            className="img-fluid"
            style={{ objectFit: "cover", width: "100%" }}
          />

          {selectedUserData && (
            <div style={{ marginTop: 16 }}>
              <h5>DNI: {selectedUserData.dni}</h5>
              <h5>Nombre: {selectedUserData.nombre}</h5>
              <h5>Alta: {selectedUserData.alta?.split('T')[0]}</h5>

              {feedback && (
                <Alert
                  variant={feedback.type}
                  onClose={() => setFeedback(null)}
                  dismissible
                  style={{ fontSize: 13, marginTop: 12 }}
                >
                  <div>{feedback.message}</div>
                  {feedback.detail && (
                    <div style={{ fontSize: 12, marginTop: 4, color: '#555', wordBreak: 'break-all' }}>
                      {feedback.detail}
                    </div>
                  )}
                </Alert>
              )}

              {!confirmando ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                  <Button
                    variant="primary"
                    onClick={() => { setConfirmando(true); setFeedback(null); }}
                    disabled={loading || dryRunLoading}
                  >
                    Registrar Asistencia a Agencia o Sala de Juego
                  </Button>

                  {auth.esSuperadmin && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => registrarIntento(true)}
                      disabled={loading || dryRunLoading}
                      style={{ border: '1.5px dashed #bbb', color: '#888' }}
                    >
                      {dryRunLoading ? <Spinner animation="border" size="sm" /> : "Probar (sin enviar)"}
                    </Button>
                  )}

                  <Button variant="outline-primary" onClick={() => setShowProfileModal(true)}>
                    Ver Perfil Completo
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 12, padding: 14, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
                  <p style={{ marginBottom: 12, fontWeight: 500 }}>
                    ¿Confirmar registro de intento de ingreso para <strong>{selectedUserData.nombre}</strong>, DNI <strong>{selectedUserData.dni}</strong>?
                  </p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Button
                      variant="danger"
                      onClick={() => registrarIntento(false)}
                      disabled={loading}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : "Sí, confirmar"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setConfirmando(false)}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ProfileModal
        show={showProfileModal}
        handleClose={() => setShowProfileModal(false)}
        dni={selectedUserData?.dni}
      />
    </div>
  );
}

const Pagination = ({ photosPerPage, totalPhotos, paginate, currentPage }) => {
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(totalPhotos / photosPerPage); i++) {
    pageNumbers.push(i);
  }
  return (
    <nav>
      <ul className="pagination">
        {pageNumbers.map((number) => (
          <li key={number} className={number === currentPage ? 'page-item active' : 'page-item'}>
            <button onClick={() => paginate(number)} className="page-link">
              {number}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default AutoexcluidosGallery;
